import {
  KnowledgeFacetAggregate,
  LIBRARY_FACET_RULE_FIELD_KINDS,
  LibraryCollectionRules,
  LibraryFacetRuleField,
  marketMatchesFilter,
} from '@digital-twins/shared-types'

/** Library filter kind (facet kind) to its collection rule field: market to markets, and so on. */
const RULE_FIELD_BY_KIND: Record<string, LibraryFacetRuleField> = Object.fromEntries(
  (Object.entries(LIBRARY_FACET_RULE_FIELD_KINDS) as Array<[LibraryFacetRuleField, string]>).map(
    ([field, kind]) => [kind, field],
  ),
)

const UNKNOWN_FILTER_VALUE = '__unknown__'

export interface SavedFilterRulesResult {
  rules: LibraryCollectionRules
  /** At least one filter became a rule: Save is possible. */
  hasRules: boolean
  /** Active filters a rule cannot carry (type, owner, source, "Unknown", a value without tokens). */
  droppedKinds: Array<string>
}

/**
 * The facet value tokens behind every catalog value the Library filter
 * matches for `value`, with the same predicate as `documentMatchesFacetFilters`:
 * a market matches its country aliases ("US" and "United States") and a
 * region matches its countries (`marketMatchesFilter`), so the rule holds
 * every token the filtered view reads; any other kind matches its value text,
 * case insensitively.
 */
function catalogTokensFor(
  kind: string,
  value: string,
  facetCatalog: Array<KnowledgeFacetAggregate>,
): Array<string> {
  const options = facetCatalog.find((facet) => facet.kind === kind)?.values ?? []
  const wanted = value.trim().toLowerCase()
  const matching =
    kind === 'market'
      ? options.filter((option) => marketMatchesFilter(option.value, value))
      : options.filter((option) => option.value.trim().toLowerCase() === wanted)
  return [...new Set(matching.flatMap((option) => option.tokens ?? []))].sort()
}

/**
 * The Library's active filters as collection rules (D1, saved filter): each
 * rule-able filter (market, year, method, category, brand) becomes the facet
 * value tokens behind the catalog values it matches, read from
 * `facetCatalog[].values[].tokens` (`catalogTokensFor`); the rest is reported
 * as dropped.
 */
export function savedFilterRules(
  filters: Record<string, string | null>,
  facetCatalog: Array<KnowledgeFacetAggregate>,
): SavedFilterRulesResult {
  const rules: LibraryCollectionRules = { version: 1 }
  const droppedKinds: Array<string> = []
  let hasRules = false

  for (const [kind, value] of Object.entries(filters)) {
    if (!value) {
      continue
    }
    const field = RULE_FIELD_BY_KIND[kind]
    if (!field || value === UNKNOWN_FILTER_VALUE) {
      droppedKinds.push(kind)
      continue
    }
    const tokens = catalogTokensFor(kind, value, facetCatalog)
    if (tokens.length === 0) {
      droppedKinds.push(kind)
      continue
    }
    rules[field] = tokens
    hasRules = true
  }

  return { rules, hasRules, droppedKinds }
}
